import { useCallback, useContext, useMemo } from 'react';
import { createId } from '@paralleldrive/cuid2';
import { ChatFileUploadStatus, ChatQueryStatus, ChatResponseType } from '@trinity/chat';
import type { Chat } from '@trinity/chat';
import {
  SubmitMessageContext,
  ChatSessionInputContext,
  ChatSessionFileUploadsContext,
  IsPreparingUploadContext,
  ChatHasAccessContext,
  ChatSessionQueriesContext,
} from './context';
import { useSelectedQueryParentIdentifier } from './hooks/useSelectedQueryParentIdentifier';
import { useSubmitQueryMutation } from './hooks/useSubmitQueryMutation';

type Props = {
  children: React.ReactNode;
};

/**
 * Check if there are any queries currently being processed (loading or streaming)
 */
const hasAnyQueryInProgress = (queries: Record<string, Chat.Query>): boolean => {
  if (!queries) return false;

  return Object.values(queries).some(query =>
    query.status === ChatQueryStatus.Loading ||
    query.status === ChatQueryStatus.LoadingExtended ||
    query.status === ChatQueryStatus.Streaming,
  );
};

/**
 * Check if there's an active workflow being conducted
 * A workflow is considered active if it's a FormWorkflow response and not yet completed
 */
const hasActiveWorkflow = (queries: Record<string, Chat.Query>): boolean => {
  if (!queries) return false;

  return Object.values(queries).some(query => {
    // Check if it's a FormWorkflow response
    if (query.response?.type !== ChatResponseType.FormWorkflow) {
      return false;
    }

    // Get the state from the query
    const state = query.state as Chat.QueryState.FormWorkflow;

    // Workflow is active if it's not in a final state
    return state.status !== 'form-submitted' && state.status !== 'form-completion';
  });
};

export const SubmitMessageContainer = (props: Props) => {
  const input = useContext(ChatSessionInputContext);
  const fileUploads = useContext(ChatSessionFileUploadsContext);
  const queries = useContext(ChatSessionQueriesContext);
  const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();
  const hasAccess = useContext(ChatHasAccessContext);

  const { isPending, mutateAsync } = useSubmitQueryMutation();
  const isPreparingUpload = useContext(IsPreparingUploadContext);

  const handleSubmit = useCallback(() => {
    const identifier = createId();
    const parentQueryIdentifier = getSelectedQueryParentIdentifier();

    const query = input;

    return mutateAsync({
      action: null,
      identifier,
      parentQueryIdentifier,
      query,
    });
  }, [
    getSelectedQueryParentIdentifier,
    input,
    mutateAsync,
  ]);

  const canSubmit = useMemo(() => {
    const hasQueryInProgress = hasAnyQueryInProgress(queries);
    // const hasWorkflowActive = hasActiveWorkflow(queries);

    return input.trim().length > 0 &&
      hasAccess &&
      !isPending &&
      !isPreparingUpload &&
      !hasQueryInProgress &&
      // !hasWorkflowActive &&
      !fileUploads.some(s => s.items.some(i => i.status !== ChatFileUploadStatus.Ready));
  }, [fileUploads, hasAccess, input, isPending, isPreparingUpload, queries]);

  const submitCtx = useMemo(() => ({
    canSubmit,
    isSubmitting: isPending,
    onSubmit: handleSubmit,
  }), [canSubmit, isPending, handleSubmit]);

  return (
    <SubmitMessageContext.Provider value={submitCtx}>
      {props.children}
    </SubmitMessageContext.Provider>
  );
};